import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Mail, ArrowRight, Inbox } from "lucide-react";
import { t, useLang } from "../lib/i18n";
import { ReadsLogo } from "../components/ReadsLogo";

/**
 * Post-signup "check your email" landing page.
 *
 * We land here after `POST /api/auth/register` for the standard flow — the
 * account exists but hasn't been verified yet. The route accepts an
 * optional `email` in either the URL search-string OR router state so we
 * can echo it back to the user (helps them realise if they typed the
 * wrong address).
 */
export default function VerifyEmailSentPage() {
  useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const email = params.get("email") || location.state?.email || "";

  // If a signed-in user lands here after already verifying, bounce home.
  useEffect(() => {
    // Nothing to guard here — the page is safe for anyone to see.
  }, [navigate]);

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16 bg-[#F0EAD6]" data-testid="verify-email-sent-page">
      <div className="max-w-md w-full">
        <div className="mb-8 flex items-center justify-center">
          <ReadsLogo size={40} />
        </div>
        <div className="bg-[#FAF5E6] border border-[rgba(10,10,10,0.10)] p-8 md:p-10 fade-up text-center">
          <div className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-[#F2EAD3] border border-[rgba(10,10,10,0.10)] mx-auto mb-6">
            <Inbox className="w-6 h-6 text-[#0A0A0A]" strokeWidth={1.5} />
          </div>
          <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#4A4030] mb-3">One more step</div>
          <h1 className="font-heading text-3xl md:text-4xl text-[#0A0A0A] tracking-tight leading-tight mb-4">
            Check your inbox
          </h1>
          <p className="text-[#4A4030] leading-relaxed mb-2">
            We&apos;ve sent a verification link {email ? <>to <strong className="text-[#0A0A0A] break-all">{email}</strong></> : "to your inbox"}. Click it to activate your account and start logging books.
          </p>
          <p className="text-[#525252] text-sm mt-4 leading-relaxed">
            Can&apos;t find it? Check your spam folder — the sender is <span className="font-mono text-xs">no-reply@readsco.org</span>. The link is valid for 24 hours.
          </p>

          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-[10px] px-5 py-2.5 rounded-sm transition-colors"
              data-testid="verify-goto-login"
            >
              Go to log in <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-1.5 border border-[rgba(10,10,10,0.18)] bg-[#F2EAD3] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-[10px] px-5 py-2.5 rounded-sm transition-colors"
              data-testid="verify-need-help"
            >
              <Mail className="w-3 h-3" strokeWidth={1.5} /> Didn&apos;t arrive?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
